import { gql } from '@apollo/client';

const VOICE_AGENT_FRAGMENT = gql`
    fragment VoiceAgentFields on VoiceAgentConfig {
        id
        name
        enabled
        companyName
        companyDescription
        systemPrompt
        additionalPrompt
        editablePromptEnabled
        editablePrompt {
            id
            role
            guardrails
            formality
            approach
            phases {
                goal
                entry
                procedure
                response
                nextStep
            }
            generalRemarks
        }
        consentQuestion
        introSpeech
        introSpeechPersonalized
        language
        phoneNumber
        transferPhoneNumber
        fallbackEmail
        sendEmailAllowedDomains
        callerIdentificationLookupDefinitionId
        orderHistoryLookupDefinitionId
        orderLookupDefinitionId
        articleLookupDefinitionId
        erpToolProviderKey
        erpToolProviderIntegrationId
        requireIdentifiedCaller
        callerRejectionMessage
        requireCallerVerification
        callerVerificationField
        callerVerificationExtraDataKey
        callerVerificationQuestion
        callerVerificationMaxAttempts
        callerVerificationFailedMessage
        backgroundVoiceDetection
        voiceModel
        voiceSpeed
        maxCallDurationSeconds
        forwardOnConsentDenial
        webCallRequestsEnabled
        webCallAllowedPrefixes
        webCallDailyCap
        webCallCooldownSeconds
        webCallAskConsentQuestion
        calendlyMeetingLink
        calendlySummaryQuestion
        calendlyApiTokenSet
        summaryEmailEnabled
        summaryEmailRecipients
        summaryTeamsEnabled
        summaryTeamsTeamId
        summaryTeamsTeamName
        summaryTeamsChannelId
        summaryTeamsChannelName
        enabledTools
        defaultChannels {
            orderChannelId
            rfqChannelId
        }
        documentFields {
            order {
                headerFields
                lineItemFields
            }
            rfq {
                headerFields
                lineItemFields
            }
        }
        team {
            id
            name
            isDefault
        }
        knowledgeFiles {
            id
            name
        }
        agentLink
    }
`;

// The list only renders what the table columns show — transcripts, summaries and audio are loaded
// per record by GET_VOICE_AGENT_RECORD when the detail panel opens.
const VOICE_AGENT_RECORD_LIST_FRAGMENT = gql`
    fragment VoiceAgentRecordListFields on VoiceAgentRecord {
        id
        createdAt
        callStartedAt
        finishedAt
        callerName
        callerPhoneNumber
        errorMessage
        callOutcome
        endReason
        sentiment
        documents {
            id
        }
    }
`;

const VOICE_AGENT_RECORD_FRAGMENT = gql`
    fragment VoiceAgentRecordFields on VoiceAgentRecord {
        id
        createdAt
        callStartedAt
        finishedAt
        conversationId
        callerName
        callerPhoneNumber
        calledNumber
        errorMessage
        callOutcome
        endReason
        sentiment
        summary
        summaryDelivery {
            email {
                status
                recipients
                deliveredRecipients
                attempts
                error
                deliveredAt
            }
            teams {
                status
                recipients
                deliveredRecipients
                attempts
                error
                deliveredAt
            }
        }
        audioRecordingUrl
        transcript {
            entries {
                role
                message
                time
            }
        }
        documents {
            id
            documentType
            subject
        }
    }
`;

// Queries

export const GET_VOICE_AGENTS = gql`
    ${VOICE_AGENT_FRAGMENT}
    query GetVoiceAgents {
        voiceAgents {
            ...VoiceAgentFields
        }
    }
`;

export const GET_VOICE_AGENT = gql`
    ${VOICE_AGENT_FRAGMENT}
    query GetVoiceAgent($id: UUID!) {
        voiceAgent(id: $id) {
            ...VoiceAgentFields
        }
    }
`;

export const GET_VOICE_AGENT_RECORDS = gql`
    ${VOICE_AGENT_RECORD_LIST_FRAGMENT}
    query GetVoiceAgentRecords($voiceAgentId: UUID, $offset: Int, $limit: Int) {
        voiceAgentRecordsList(voiceAgentId: $voiceAgentId, offset: $offset, limit: $limit) {
            totalCount
            records {
                ...VoiceAgentRecordListFields
            }
        }
    }
`;

export const GET_VOICE_AGENT_RECORD = gql`
    ${VOICE_AGENT_RECORD_FRAGMENT}
    query GetVoiceAgentRecord($id: UUID!) {
        voiceAgentRecord(id: $id) {
            ...VoiceAgentRecordFields
        }
    }
`;

export const GET_VOICE_AGENT_AVAILABLE_DOCUMENT_FIELDS = gql`
    query GetVoiceAgentAvailableDocumentFields {
        voiceAgentAvailableDocumentFields {
            order {
                headerFields {
                    key
                    label
                }
                lineItemFields {
                    key
                    label
                }
            }
            rfq {
                headerFields {
                    key
                    label
                }
                lineItemFields {
                    key
                    label
                }
            }
        }
    }
`;

// What a new agent's editable prompt starts with, per language: the role starter text, the preselected guardrails
// and the flow templates behind "Use template" / "Add phase" (ENG-11790).
export const GET_VOICE_AGENT_EDITABLE_PROMPT_DEFAULTS = gql`
    query GetVoiceAgentEditablePromptDefaults($language: String) {
        voiceAgentEditablePromptDefaults(language: $language) {
            role
            defaultGuardrails
            flowTemplates {
                key
                addOn
                phases {
                    goal
                    entry
                    procedure
                    response
                    nextStep
                }
            }
        }
    }
`;

// Legacy: the single free-text template of the old prompt editor. No longer used by the Prompt tab.
export const GET_VOICE_AGENT_PROMPT_TEMPLATE = gql`
    query GetVoiceAgentPromptTemplate($language: String) {
        voiceAgentPromptTemplate(language: $language)
    }
`;

// Mutations

export const CREATE_VOICE_AGENT = gql`
    ${VOICE_AGENT_FRAGMENT}
    mutation CreateVoiceAgent($input: CreateVoiceAgentInput!) {
        createVoiceAgent(input: $input) {
            voiceAgent {
                ...VoiceAgentFields
            }
        }
    }
`;

export const UPDATE_VOICE_AGENT = gql`
    ${VOICE_AGENT_FRAGMENT}
    mutation UpdateVoiceAgent($id: UUID!, $input: UpdateVoiceAgentInput!) {
        updateVoiceAgent(id: $id, input: $input) {
            voiceAgent {
                ...VoiceAgentFields
            }
        }
    }
`;

export const DELETE_VOICE_AGENT = gql`
    mutation DeleteVoiceAgent($id: UUID!) {
        deleteVoiceAgent(id: $id) {
            success
        }
    }
`;

export const SET_VOICE_AGENT_ENABLED = gql`
    mutation SetVoiceAgentEnabled($id: UUID!, $enabled: Boolean!) {
        setVoiceAgentEnabled(id: $id, enabled: $enabled) {
            voiceAgent {
                id
                enabled
            }
        }
    }
`;

export const UPLOAD_VOICE_AGENT_KNOWLEDGE_FILE = gql`
    mutation UploadVoiceAgentKnowledgeFile($configId: UUID!, $file: Upload!) {
        uploadVoiceAgentKnowledgeFile(configId: $configId, file: $file) {
            knowledgeFile {
                id
                name
            }
        }
    }
`;

// Rewrites the system prompt from a free-text instruction. Nothing is persisted — the suggestion is returned
// for the user to review, and saved with the regular update mutation.
export const IMPROVE_VOICE_AGENT_PROMPT = gql`
    mutation ImproveVoiceAgentPrompt($input: ImproveVoiceAgentPromptInput!) {
        improveVoiceAgentPrompt(input: $input) {
            suggestion {
                prompt
                summary
            }
        }
    }
`;

// Workist only: the agent's settings as a JSON file, and the reverse. ElevenLabs ids, secrets, the team and
// the phone number are never part of the file — the importing agent keeps its own.
export const EXPORT_VOICE_AGENT_CONFIG = gql`
    mutation ExportVoiceAgentConfig($id: UUID!) {
        exportVoiceAgentConfig(id: $id) {
            success
            data
        }
    }
`;

export const IMPORT_VOICE_AGENT_CONFIG = gql`
    mutation ImportVoiceAgentConfig($id: UUID!, $file: Upload!) {
        importVoiceAgentConfig(id: $id, file: $file) {
            success
        }
    }
`;

export const DELETE_VOICE_AGENT_KNOWLEDGE_FILE = gql`
    mutation DeleteVoiceAgentKnowledgeFile($configId: UUID!, $documentationId: String!) {
        deleteVoiceAgentKnowledgeFile(configId: $configId, documentationId: $documentationId) {
            success
        }
    }
`;

// Microsoft Teams integration (used by the Teams voice-agent tools)

export const GET_TEAMS_INTEGRATION = gql`
    query GetTeamsIntegration {
        teamsIntegration {
            id
            enabled
            accountEmail
            isConnected
            isAdminConsentGranted
            supportsChannelMessages
        }
    }
`;

// Teams and channels the connected account can post into — the channel picker of the call summaries.
export const GET_TEAMS_CHANNELS = gql`
    query GetTeamsChannels {
        teamsChannels {
            error
            teams {
                id
                name
                channels {
                    id
                    name
                }
            }
        }
    }
`;

export const CONNECT_TEAMS_INTEGRATION = gql`
    mutation ConnectTeamsIntegration {
        connectTeamsIntegration {
            redirectUrl
            success
        }
    }
`;

export const GET_TEAMS_ADMIN_CONSENT_URL = gql`
    mutation GetTeamsAdminConsentUrl {
        getTeamsAdminConsentUrl {
            adminConsentUrl
        }
    }
`;

export const DELETE_TEAMS_INTEGRATION = gql`
    mutation DeleteTeamsIntegration {
        deleteTeamsIntegration {
            success
        }
    }
`;
